import type { AuditActorFacet, AuditEventEnvelope } from '@/types';
import { formatAuditValue } from '@/utils/formatters';

/** The one event type the Status view, the status filter and byStatus are about. */
export const STATUS_CHANGED_EVENT = 'deposit.status_changed';

export interface CategoryMeta {
  label: string;
  icon: string;
  color: string;
  bg: string;
  barBg: string;
}

// Ordered so the distribution bar and legend read consistently.
export const CATEGORY_ORDER = [
  'Deposits',
  'Files',
  'Notes',
  'Periods',
  'Imports',
  'Exports',
  'Reconciliation',
  'Clients',
  'Admin',
  'Session',
  'Security',
] as const;

export const CATEGORY_META: Record<string, CategoryMeta> = {
  Security: { label: 'Security', icon: 'fa-shield-halved', color: 'text-red-600', bg: 'bg-red-100', barBg: 'bg-red-400' },
  Session: { label: 'Session', icon: 'fa-right-to-bracket', color: 'text-sky-600', bg: 'bg-sky-100', barBg: 'bg-sky-400' },
  Deposits: { label: 'Deposits', icon: 'fa-dollar-sign', color: 'text-green-600', bg: 'bg-green-100', barBg: 'bg-green-400' },
  Files: { label: 'Files', icon: 'fa-file-lines', color: 'text-amber-600', bg: 'bg-amber-100', barBg: 'bg-amber-400' },
  Notes: { label: 'Notes', icon: 'fa-note-sticky', color: 'text-yellow-600', bg: 'bg-yellow-100', barBg: 'bg-yellow-400' },
  Periods: { label: 'Periods', icon: 'fa-calendar', color: 'text-orange-600', bg: 'bg-orange-100', barBg: 'bg-orange-400' },
  Imports: { label: 'Imports', icon: 'fa-file-import', color: 'text-blue-600', bg: 'bg-blue-100', barBg: 'bg-blue-400' },
  Exports: { label: 'Exports', icon: 'fa-file-export', color: 'text-teal-600', bg: 'bg-teal-100', barBg: 'bg-teal-400' },
  Reconciliation: { label: 'Recon', icon: 'fa-check-double', color: 'text-purple-600', bg: 'bg-purple-100', barBg: 'bg-purple-400' },
  Admin: { label: 'Admin', icon: 'fa-user-gear', color: 'text-gray-600', bg: 'bg-gray-100', barBg: 'bg-gray-400' },
  Clients: { label: 'Clients', icon: 'fa-building', color: 'text-indigo-600', bg: 'bg-indigo-100', barBg: 'bg-indigo-400' },
};

export function categoryMeta(category: string): CategoryMeta {
  return CATEGORY_META[category] ?? { label: category, icon: 'fa-circle', color: 'text-gray-600', bg: 'bg-gray-100', barBg: 'bg-gray-400' };
}

/** Red-tinted rows: failed/blocked/denied/unauthorized security & session events. */
export function isSecurityRow(e: Pick<AuditEventEnvelope, 'category' | 'eventType'>): boolean {
  if (e.category === 'Security') return true;
  return /failed|blocked|denied|unauthorized|invalid/i.test(e.eventType);
}

export const UNAUTHENTICATED_EVENT = 'security.unauthorized_access';

/** "GET /api/clients/…" from an unauthorized-access row's reason text, if present. */
export function blockedRequestOf(e: Pick<AuditEventEnvelope, 'details'>): string | null {
  const reason = (e.details as Record<string, unknown> | null)?.reason;
  if (typeof reason !== 'string') return null;
  const m = reason.match(/attempt to (\S+ \S+)/);
  return m ? m[1] : null;
}

// Paths no page of this app requests: the classic scanner targets.
const PROBE_PATH = /\.(php|aspx?|jsp|cgi|env|git|sql|bak|ini)\b|wp-|phpmyadmin|\/\.(env|git)/i;

/**
 * A blocked request with no valid sign-in is almost always this app's own page calling the
 * API after its sign-in lapsed (measured 2026-09-30: nearly all of production's entries were
 * ordinary browser requests to the app's own routes). Only a script-like client or a path the
 * app never requests is worth flagging as a possible probe.
 */
export function isPossibleProbe(e: Pick<AuditEventEnvelope, 'details' | 'userAgent'>): boolean {
  const ua = e.userAgent ?? '';
  if (!/^Mozilla\//.test(ua)) return true;
  const req = blockedRequestOf(e);
  const path = req ? req.split(' ')[1] ?? '' : '';
  return !path.startsWith('/api/') || PROBE_PATH.test(path);
}

/**
 * Rows that deserve the red warning styling. Security events do — except a blocked
 * signed-out request that looks like the app's own traffic, which is routine, not an attack.
 */
export function isAlertRow(e: Pick<AuditEventEnvelope, 'category' | 'eventType' | 'details' | 'userAgent'>): boolean {
  if (e.eventType === UNAUTHENTICATED_EVENT) return isPossibleProbe(e);
  return isSecurityRow(e);
}

const VERBS: Record<string, string> = {
  'session.login': 'signed in',
  'session.login.failed': 'failed to sign in',
  'session.login.blocked_domain': 'was blocked (unauthorized domain)',
  'session.login.blocked_inactive': 'was blocked (inactive account)',
  'session.logout.manual': 'signed out',
  'session.logout.idle': 'was signed out (idle timeout)',
  'session.expired': 'session expired',
  'security.permission_denied': 'was denied permission',
  'security.unauthorized_access': '— request blocked (no valid sign-in)',
  'security.token_invalid': 'presented an invalid token',
  'deposit.created': 'created deposit',
  'deposit.updated': 'updated deposit',
  'deposit.deleted': 'deleted deposit',
  'deposit.status_changed': 'changed status',
  'deposit.allocated': 'updated allocation on',
  'deposit.split': 'split deposit',
  'deposit.grouped': 'grouped deposits',
  'deposit.ungrouped': 'ungrouped deposits',
  'deposit.assigned': 'assigned deposit',
  'deposit.unassigned': 'unassigned deposit',
  'deposit.forward_balance': 'set forward balance on',
  'deposit.viewed': 'viewed deposit',
  'deposit.searched': 'searched deposits',
  'deposit.entry_period_assigned': 'assigned an entry period',
  'file.uploaded': 'uploaded a file',
  'file.viewed': 'viewed a file',
  'file.deleted': 'deleted a file',
  'note.added': 'added a note',
  'period.created': 'created entry period',
  'period.closed': 'closed entry period',
  'period.reopened': 'reopened entry period',
  'period.gap_autocreated': 'auto-created gap periods',
  'import.csv': 'imported deposits (CSV)',
  'import.duplicate_detected': 'detected duplicates',
  'exports.deposits_csv': 'exported deposits (CSV)',
  'exports.bulk_csv': 'bulk-exported (CSV)',
  'exports.reconciliation': 'exported reconciliation',
  'exports.audit_csv': 'exported the audit log (CSV)',
  'recon.completed': 'completed reconciliation',
  'recon.validated': 'validated reconciliation',
  'recon.viewed': 'viewed reconciliation',
  'admin.user.activated': 'activated a user',
  'admin.user.deactivated': 'deactivated a user',
  'admin.user.role_changed': 'changed a user role',
  'admin.user.client_access_changed': "changed a user's client access",
  'admin.user.created': 'created a user',
  'admin.user.deleted': 'deleted a user',
  'admin.permission_override.set': 'set permission overrides for',
  'admin.permission_override.cleared': 'cleared permission overrides for',
  'client.switched': 'switched to client',
  'client.created': 'created client',
  'client.updated': 'updated client',
  'audit.viewed': 'viewed the audit log',
};

function humanize(eventType: string): string {
  const last = eventType.split('.').slice(1).join(' ') || eventType;
  return last.replace(/[._]/g, ' ');
}

// ---------------------------------------------------------------------------------------
// Plain-language labels (dropdowns, chips, legends). Raw values stay available as tooltips.
// ---------------------------------------------------------------------------------------

const EVENT_TYPE_LABELS: Record<string, string> = {
  // Deposits
  'deposit.status_changed': 'Status changed',
  'deposit.created': 'Deposit created',
  'deposit.updated': 'Deposit edited',
  'deposit.deleted': 'Deposit deleted',
  'deposit.allocated': 'Allocation updated',
  'deposit.split': 'Deposit split',
  'deposit.grouped': 'Deposits grouped',
  'deposit.ungrouped': 'Deposits ungrouped',
  'deposit.assigned': 'Deposit assigned',
  'deposit.unassigned': 'Deposit unassigned',
  'deposit.forward_balance': 'Forward balance set',
  'deposit.viewed': 'Deposit viewed',
  'deposit.searched': 'Deposits searched',
  'deposit.entry_period_assigned': 'Entry period assigned',
  // Files & notes
  'file.uploaded': 'File uploaded',
  'file.viewed': 'File viewed',
  'file.deleted': 'File deleted',
  'file.external_open': 'File opened in Drive',
  'note.added': 'Note added',
  // Periods
  'period.created': 'Period created',
  'period.closed': 'Period closed',
  'period.reopened': 'Period reopened',
  'period.gap_autocreated': 'Gap periods auto-created',
  // Imports / exports
  'import.csv': 'CSV import',
  'import.duplicate_detected': 'Duplicates detected',
  'exports.deposits_csv': 'Deposits exported (CSV)',
  'exports.bulk_csv': 'Bulk export (CSV)',
  'exports.reconciliation': 'Reconciliation exported',
  'exports.audit_csv': 'Audit log exported',
  // Reconciliation
  'recon.completed': 'Reconciliation completed',
  'recon.validated': 'Reconciliation validated',
  'recon.viewed': 'Reconciliation viewed',
  // Sessions
  'session.login': 'Signed in',
  'session.login.failed': 'Sign-in failed',
  'session.login.blocked_domain': 'Sign-in blocked (domain)',
  'session.login.blocked_inactive': 'Sign-in blocked (inactive)',
  'session.logout': 'Signed out',
  'session.logout.manual': 'Signed out',
  'session.logout.idle': 'Signed out (idle)',
  'session.expired': 'Session expired',
  'session.google_token_revoked': 'Google access revoked',
  'session.google_token_revoke_failed': 'Google revoke failed',
  // Security
  'security.permission_denied': 'Permission denied',
  'security.unauthorized_access': 'Blocked request (not signed in)',
  'security.token_invalid': 'Invalid token',
  'security.mfa_success': 'MFA passed',
  'security.mfa_failed': 'MFA failed',
  'security.cross_client_access_denied': 'Cross-client access denied',
  'security.cross_client_file_denied': 'Cross-client file denied',
  'security.cross_client_period_denied': 'Cross-client period denied',
  'security.client_context_denied': 'Client access denied',
  // Admin
  'admin.user.activated': 'User activated',
  'admin.user.deactivated': 'User deactivated',
  'admin.user.role_changed': 'User role changed',
  'admin.user.client_access_changed': 'Client access changed',
  'admin.user.created': 'User created',
  'admin.user.deleted': 'User deleted',
  'admin.permission_override.set': 'Permission override set',
  'admin.permission_override.cleared': 'Permission override cleared',
  'admin.allowlist.email_added': 'Allowlist email added',
  'admin.allowlist.email_updated': 'Allowlist email updated',
  'admin.allowlist.email_deleted': 'Allowlist email removed',
  'admin.allowlist.domain_added': 'Allowlist domain added',
  'admin.allowlist.domain_updated': 'Allowlist domain updated',
  'admin.allowlist.domain_deleted': 'Allowlist domain removed',
  // Clients / audit
  'client.switched': 'Client switched',
  'client.created': 'Client created',
  'client.updated': 'Client updated',
  'audit.viewed': 'Audit log viewed',
};

/** Plain-language event-type label, e.g. deposit.status_changed → "Status changed". */
export function eventTypeLabel(eventType: string): string {
  const known = EVENT_TYPE_LABELS[eventType];
  if (known) return known;
  // Fallback: "foo.bar_baz" → "Foo bar baz".
  const words = String(eventType ?? '')
    .replace(/[._]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!words) return 'Unknown event';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Standard deposit statuses, in the order the Status picker lists them. */
export const STANDARD_STATUSES = ['Completed', 'Pending', 'ForwardBalance', 'FBCompleted', 'NotBillingRelated', 'New'] as const;

interface StatusMeta {
  label: string;
  /** tailwind bg-* for the bar segment + legend dot (deposit status colors). */
  barBg: string;
}

const STATUS_META: Record<string, StatusMeta> = {
  Completed: { label: 'Completed', barBg: 'bg-green-500' },
  Pending: { label: 'Pending', barBg: 'bg-yellow-400' },
  ForwardBalance: { label: 'Forward Balance', barBg: 'bg-orange-400' },
  FBCompleted: { label: 'FB Completed', barBg: 'bg-teal-500' },
  NotBillingRelated: { label: 'Not Billing Related', barBg: 'bg-purple-400' },
  New: { label: 'New', barBg: 'bg-gray-400' },
};

/** Human status label, e.g. ForwardBalance → "Forward Balance". */
export function statusLabel(status: string): string {
  const known = STATUS_META[status];
  if (known) return known.label;
  // Fallback: split camelCase / snake_case into words ("SomeNewStatus" → "Some New Status").
  const words = String(status ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return words || 'Unknown';
}

export function statusBarBg(status: string): string {
  return STATUS_META[status]?.barBg ?? 'bg-slate-400';
}

/** Dollar format used across the app: `$1,234.56` (sign after `$` for negatives). */
export function formatUsd(n: number | null | undefined): string {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---------------------------------------------------------------------------------------
// Actors
// ---------------------------------------------------------------------------------------

type ActorFields = Pick<AuditEventEnvelope, 'actorUserId' | 'actorEmail' | 'actorType'> & { actorKey?: string | null };

/**
 * The backend's actor-key rule (auditEventsController `actorKeyOf`), reproduced exactly:
 * 'u:'+actorUserId if non-empty; else 'e:'+lower(actorEmail) if non-empty (an empty string
 * counts as missing); else 'system' if actorType is 'system'; else 'anon'.
 */
export function deriveActorKey(a: { actorUserId?: string | null; actorEmail?: string | null; actorType?: string | null }): string {
  if (a.actorUserId) return `u:${a.actorUserId}`;
  if (a.actorEmail) return `e:${a.actorEmail.toLowerCase()}`;
  return a.actorType === 'system' ? 'system' : 'anon';
}

/** Label for requests that reached the server with no valid sign-in (no user to name). */
export const NOT_SIGNED_IN = 'Not signed in';

/** The event's actor key. Prefers the server's `actorKey`; derives it with the backend rule if absent. */
export function actorKeyOf(e: ActorFields): string {
  return e.actorKey || deriveActorKey(e);
}

/** Display name for an event's actor (bold name at the start of the sentence). */
export function actorDisplayName(e: Pick<AuditEventEnvelope, 'actorType' | 'actorName' | 'actorEmail'>): string {
  if (e.actorType === 'system') return 'System';
  return e.actorName || e.actorEmail?.split('@')[0] || NOT_SIGNED_IN;
}

type ActorFacetLike = Pick<AuditActorFacet, 'actorUserId' | 'actorEmail' | 'name' | 'company' | 'count'> & {
  key?: string | null;
  actorType?: string | null;
};

/**
 * A facet entry's key. Prefers the server's `key`; otherwise the backend rule (deriveActorKey).
 * Facet entries carry no actorType in the contract, so a missing one is inferred from the
 * 'System' company tag the backend gives system actors.
 */
export function actorFacetKey(a: ActorFacetLike): string {
  if (a.key) return a.key;
  const actorType = a.actorType ?? (a.company === 'System' ? 'system' : null);
  return deriveActorKey({ actorUserId: a.actorUserId, actorEmail: a.actorEmail, actorType });
}

/** Full name for a facet entry: "System" / "Not signed in" for the no-user keys. */
export function actorFacetName(a: ActorFacetLike): string {
  const key = actorFacetKey(a);
  if (key === 'system') return 'System';
  if (key === 'anon') return NOT_SIGNED_IN;
  return a.name || a.actorEmail || NOT_SIGNED_IN;
}

/** Short name for legends: name, else the email's local part. */
export function actorFacetShortName(a: ActorFacetLike): string {
  const key = actorFacetKey(a);
  if (key === 'system') return 'System';
  if (key === 'anon') return NOT_SIGNED_IN;
  return a.name || a.actorEmail?.split('@')[0] || NOT_SIGNED_IN;
}

/** Picker label: "Name · Company (count)". */
export function actorFacetLabel(a: ActorFacetLike): string {
  const name = actorFacetName(a);
  const key = actorFacetKey(a);
  const company = a.company && a.company !== name && key !== 'system' && key !== 'anon' ? ` · ${a.company}` : '';
  return `${name}${company} (${(a.count ?? 0).toLocaleString()})`;
}

/** Best-effort label for an actor key with no facet entry (e.g. restored from a link). */
export function actorKeyFallbackLabel(key: string): string {
  if (key === 'system') return 'System';
  if (key === 'anon') return NOT_SIGNED_IN;
  if (key.startsWith('e:')) return key.slice(2);
  return 'Selected person';
}

// ---------------------------------------------------------------------------------------
// Status changes + bursts
// ---------------------------------------------------------------------------------------

/**
 * Raw {from, to} of a deposit.status_changed event's details — the backend's `statusPair` rule:
 * details.from / details.to first, else the first details.changes entry whose field is
 * 'status' or 'value' (processTransaction writes 'value', logStatusChange writes 'status').
 */
export function statusChangeOf(e: Pick<AuditEventEnvelope, 'eventType' | 'details'>): { from: unknown; to: unknown } | null {
  if (e.eventType !== STATUS_CHANGED_EVENT || !e.details || typeof e.details !== 'object') return null;
  const d = e.details as Record<string, unknown> & { changes?: unknown };
  const ch = Array.isArray(d.changes)
    ? (d.changes as Array<{ field?: unknown; old?: unknown; new?: unknown } | null>).find(
        (c) => !!c && (c.field === 'status' || c.field === 'value')
      )
    : undefined;
  const from = d.from ?? ch?.old ?? null;
  const to = d.to ?? ch?.new ?? null;
  if (from == null && to == null) return null;
  return { from, to };
}

function formatStatusValue(v: unknown): string {
  return typeof v === 'string' ? statusLabel(v) : formatAuditValue(v);
}

/**
 * Plural phrases for collapsed bursts. `per: 'entity'` counts DISTINCT entityIds (the same
 * deposit changed twice is one deposit); `per: 'event'` counts rows (each note.added row is one
 * note, while its entityId is the deposit it was added to).
 */
const BURST_PHRASES: Record<string, { per: 'entity' | 'event'; text: (n: string) => string }> = {
  'deposit.created': { per: 'entity', text: (n) => `created ${n} deposits` },
  'deposit.updated': { per: 'entity', text: (n) => `edited ${n} deposits` },
  'deposit.deleted': { per: 'entity', text: (n) => `deleted ${n} deposits` },
  'deposit.status_changed': { per: 'entity', text: (n) => `changed status on ${n} deposits` },
  'deposit.allocated': { per: 'entity', text: (n) => `updated allocations on ${n} deposits` },
  'deposit.split': { per: 'entity', text: (n) => `split ${n} deposits` },
  'deposit.assigned': { per: 'entity', text: (n) => `assigned ${n} deposits` },
  'deposit.unassigned': { per: 'entity', text: (n) => `unassigned ${n} deposits` },
  'deposit.forward_balance': { per: 'entity', text: (n) => `set forward balance on ${n} deposits` },
  'deposit.viewed': { per: 'entity', text: (n) => `viewed ${n} deposits` },
  'deposit.entry_period_assigned': { per: 'entity', text: (n) => `assigned an entry period to ${n} deposits` },
  'file.uploaded': { per: 'entity', text: (n) => `uploaded ${n} files` },
  'file.viewed': { per: 'entity', text: (n) => `viewed ${n} files` },
  'file.deleted': { per: 'entity', text: (n) => `deleted ${n} files` },
  'note.added': { per: 'event', text: (n) => `added ${n} notes` },
};

type BurstEventFields = Pick<AuditEventEnvelope, 'eventType' | 'details'> & { entityId?: string | null };

/** Distinct non-empty entityIds in a run (falls back to the row count when none carry one). */
export function distinctEntityCount(events: readonly BurstEventFields[]): number {
  const ids = new Set(events.map((e) => e.entityId).filter((id): id is string => !!id));
  return ids.size || events.length;
}

/**
 * Predicate for a collapsed burst row (actor rendered separately), e.g. "deleted 43 deposits",
 * or "moved 12 deposits to Completed" when every change in the burst lands on the same status.
 * Deposit/file counts are DISTINCT entityIds, not rows.
 */
export function burstPhrase(events: readonly BurstEventFields[]): string {
  const eventType = events[0]?.eventType ?? '';
  const entities = distinctEntityCount(events).toLocaleString();
  if (eventType === STATUS_CHANGED_EVENT) {
    const targets = new Set(events.map((e) => statusChangeOf(e)?.to).map((t) => (t == null ? '' : String(t))));
    if (targets.size === 1) {
      const [only] = [...targets];
      if (only) return `moved ${entities} deposits to ${statusLabel(only)}`;
    }
  }
  const phrase = BURST_PHRASES[eventType];
  if (phrase) return phrase.text(phrase.per === 'entity' ? entities : events.length.toLocaleString());
  return `${VERBS[eventType] ?? humanize(eventType)} (${events.length.toLocaleString()} times)`;
}

export interface EventSentence {
  verb: string;
  /** e.g. "New → Categorized" for a status change */
  change?: string;
  /** the PHI-safe entity label to append after "on" */
  onLabel?: string | null;
}

/** Build a human sentence predicate (actor is rendered separately by the row). */
export function describeEvent(e: AuditEventEnvelope): EventSentence {
  const verb = VERBS[e.eventType] ?? humanize(e.eventType);
  let change: string | undefined;

  const d = e.details as Record<string, any> | null;
  if (e.eventType === STATUS_CHANGED_EVENT && d) {
    const sc = statusChangeOf(e);
    if (sc && sc.from != null && sc.to != null) change = `${formatStatusValue(sc.from)} → ${formatStatusValue(sc.to)}`;
  } else if (e.eventType === 'admin.user.role_changed' && d) {
    const from = d.oldValue ?? d.changes?.find((c: any) => c.field === 'role')?.old;
    const to = d.newValue ?? d.changes?.find((c: any) => c.field === 'role')?.new;
    if (from != null && to != null) change = `${formatAuditValue(from)} → ${formatAuditValue(to)}`;
  }

  // Append the entity label after the verb for deposit/file/period/client rows.
  const wantsLabel = /deposit|file|period|client|entry_period/.test(e.eventType) || e.entityType === 'deposit';
  // A blocked signed-out request names the request it blocked (method + path, no query string).
  const onLabel = e.eventType === UNAUTHENTICATED_EVENT ? blockedRequestOf(e) : wantsLabel ? e.entityLabel : null;

  return { verb, change, onLabel };
}
